import styles from './Payments.module.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import { settingsActions } from '../../store';
import { getActiveSalon } from '../../../Auth/store/selectors';
import { Cards } from './containers/Cards';
import { Invoice } from './containers/Invoice';
import { SubscriptionPlans } from './containers/SubscriptionPlans';
import { BillingSetup } from './containers/BillingSetup';
import { authActions } from '../../../Auth/store';
import dayjs from 'dayjs';
import { Check, ChevronRight, Landmark, LogOut, RefreshCw, Zap } from 'lucide-react';
import {
  PAYMENT_METHOD,
  getExpectedDebitDate,
  getInvoicePaymentStatus,
  getPayableInvoice,
  getPaymentMethodType,
  getRelevantOpenInvoice,
  invoiceHasPendingPayment,
  invoiceRequiresPayNow,
  isPaymentFailed,
  isPaymentProcessing,
  isSepaProcessing,
  isSubscriptionLocked,
} from 'utils/subscriptionGate';
import { resolvePlanCatalogIds } from './utils/subscriptionUtils';
import ConfirmModal from 'Containers/Common/ConfirmModal';
import { ROUTES } from 'routes/constants';
import { ReactComponent as Exit } from 'assets/svg/icons/esci.svg';
import {
  getCustomerBill,
  getCustomerInvoices,
  getCustomerPlan,
  getCustomerPortal,
  getCustomerProducts,
  getPlansLoading,
  getProductsError,
  getProductsLoading,
  getSubscriptionError,
  getSubscriptionStatus,
  getSubscriptionSubmitting,
} from 'Containers/Settings/store/selectors';
import Table from 'Containers/Common/CustomTable';
import { sendMessageToApp } from 'utils/exsternalCall';
import SubscriptionRenewalBanner from 'Containers/Home/containers/SubscriptionRenewalBanner';
import {
  BILLING_PATH,
  PREMIUM_UPGRADE_PATH,
  shouldBlockBillingNavigation,
} from 'utils/premiumUpgradePath';

const RECENT_COUNT = 3;

const Payments = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const products = useSelector(getCustomerProducts());
  const plansData = useSelector(getCustomerPlan());
  const invoices = useSelector(getCustomerInvoices());
  const user = useSelector(getActiveSalon());
  const billingPortal = useSelector(getCustomerPortal());
  const billInformation = useSelector(getCustomerBill());
  const subscriptionStatus = useSelector(getSubscriptionStatus());
  const subscriptionSubmitting = useSelector(getSubscriptionSubmitting());
  const subscriptionError = useSelector(getSubscriptionError());
  const productsLoading = useSelector(getProductsLoading());
  const productsError = useSelector(getProductsError());
  const plansLoading = useSelector(getPlansLoading());
  const fromApp = localStorage.getItem('fromApp') == 'true';
  // Allineato al gate di App.js (giorni <= 0): anche se isSubscribed è ancora true
  const daysLeftNum = Number(products?.giorniRimanenti);
  // Stessa regola del gate in App.js: schermo bloccante solo se davvero "locked"
  const isBillingPortal = isSubscriptionLocked(products, billInformation);
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [isRefreshingPayment, setIsRefreshingPayment] = useState(false);
  const [showAllMovements, setShowAllMovements] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const { isMobile } = useWindowSize();

  // Ghost tap post "Passa a Premium": se siamo finiti qui per errore, torna alla landing.
  useEffect(() => {
    if (shouldBlockBillingNavigation(BILLING_PATH)) {
      navigate(PREMIUM_UPGRADE_PATH, { replace: true });
    }
  }, [navigate]);

  // Fattura open rilevante (ignora unpaid storiche se ci sono pagate più recenti)
  const openInvoice = useMemo(() => getRelevantOpenInvoice(invoices), [invoices]);
  // Solo se Stripe dice che serve un pagamento cliente (non "processing")
  const payableInvoice = useMemo(() => getPayableInvoice(invoices), [invoices]);
  const unpaidInvoice = payableInvoice || openInvoice;
  const canPayNow = invoiceRequiresPayNow(payableInvoice, invoices);
  const unpaidInvoiceUrl = canPayNow ? payableInvoice?.hosted_invoice_url : null;
  const unpaidAmount =
    unpaidInvoice?.total != null ? (Number(unpaidInvoice.total) / 100).toFixed(2) : null;
  const unpaidAmountLabel = unpaidAmount ? unpaidAmount.replace('.', ',') : null;

  // Metodo di pagamento: /customer.paymentMethod; tipo da getDaysLeft → /customer → fattura
  // (payment_intent non è più espanso dal BE — usiamo solo i campi derivati)
  const paymentMethod = billInformation?.paymentMethod || null;
  const paymentMethodType =
    getPaymentMethodType(products, billInformation) ||
    openInvoice?.paymentMethodType ||
    paymentMethod?.type ||
    null;
  const hasSavedPaymentMethod = Boolean(
    paymentMethod?.id ||
      paymentMethod?.type ||
      paymentMethod?.card?.last4 ||
      paymentMethod?.sepa_debit?.last4 ||
      products?.paymentMethodType,
  );
  const collectPaymentOnConfirm =
    Boolean(
      billInformation &&
        (billInformation.id ||
          billInformation.email ||
          billInformation.name ||
          billInformation.paymentMethod !== undefined),
    ) && !hasSavedPaymentMethod;
  const isSepaMethod = paymentMethodType === PAYMENT_METHOD.SEPA;
  const unpaidPaymentStatus = getInvoicePaymentStatus(openInvoice);
  const invoicePending = invoiceHasPendingPayment(openInvoice, invoices);
  // RID in elaborazione: products e/o fattura open con PI processing
  const sepaProcessing =
    isSepaProcessing(products, billInformation) || (isSepaMethod && invoicePending);
  const expectedDebitDate = getExpectedDebitDate(openInvoice);
  // "In attesa banca" solo mentre l'app è ancora usabile; fuori grace (locked) → recovery
  const sepaWaitingInGrace = sepaProcessing && !isBillingPortal;
  // Paga ora solo con fattura pagabile e senza pagamento già in sospeso / già avviato in sessione
  const showPayNowCta = canPayNow && !sepaWaitingInGrace && !paymentStarted;

  const productsWithInvoiceFlag = {
    ...products,
    hasUnpaidInvoice: !!unpaidInvoiceUrl,
    sepaProcessing: sepaWaitingInGrace,
    subscriptionFrequency:
      billInformation?.subscriptionFrequency || openInvoice?.frequency || null,
  };
  const daysLeft = daysLeftNum;
  const showRenewalBanner =
    !canPayNow &&
    !sepaWaitingInGrace &&
    Number.isFinite(daysLeft) &&
    daysLeft > 0 &&
    daysLeft <= 3;

  const plans = plansData?.plans || [];

  const dateFormat = (timestamp) => dayjs(timestamp * 1000).format('DD/MM/YYYY');
  // Preferisci frequency.label dal BE; fallback sulla description Stripe grezza
  const movementLabel = (row) => {
    if (row?.frequency?.label) return `Abbonamento ${row.frequency.label.toLowerCase()}`;
    const raw = row?.lines?.data?.[0]?.description || '';
    const cleaned = raw.replace(/^\d+\s*[×x]\s*/i, '').replace(/\s*\(at\b.*$/i, '').trim();
    return cleaned || raw.split('00:')[0];
  };

  /** Stato riga movimento: pagata / RID in elaborazione / in attesa di pagamento. */
  const movementStatus = (row) => {
    if (row.paid) {
      return {
        label: t('settings.payments.movements.paidOn', {
          date: dayjs(row.status_transitions.paid_at * 1000).format('DD/MM'),
        }),
        pending: false,
        processing: false,
      };
    }
    const processing =
      isPaymentProcessing(row.paymentIntentStatus) &&
      row.paymentMethodType === PAYMENT_METHOD.SEPA;
    return {
      label: processing
        ? t('settings.payments.subscription.sepaProcessing.movementStatus')
        : t('settings.payments.subscription.unpaidBanner.awaitingPayment'),
      pending: true,
      processing,
    };
  };

  const scrollToRecovery = () => {
    document.getElementById('payments-recovery')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const columns = useMemo(
    () =>
      !isMobile
        ? [
            {
              Header: t('settings.payments.table.invoiceDate'),
              accessor: 'date',
              Cell: ({ value }) => dateFormat(value),
            },
            {
              Header: t('settings.payments.table.type'),
              accessor: 'lines',
              Cell: ({ row: { original: row } }) => movementLabel(row),
            },
            {
              Header: t('settings.payments.table.id'),
              accessor: 'id',
            },
            {
              Header: t('settings.payments.table.amount'),
              accessor: 'total',
              Cell: ({ value }) => (value / 100).toFixed(2).replace('.', ',') + ' €',
            },
            {
              Header: t('settings.payments.table.paymentDate'),
              accessor: 'paid',
              Cell: ({ row: { original: row } }) => {
                if (row.paid) return dayjs(row.status_transitions.paid_at * 1000).format('DD/MM/YYYY');
                const status = movementStatus(row);
                return status.processing ? (
                  <span className={styles.invoice_processing_status}>{status.label}</span>
                ) : (
                  <button
                    type="button"
                    className={styles.invoice_pending_status}
                    onClick={scrollToRecovery}
                  >
                    {status.label}
                  </button>
                );
              },
            },
          ]
        : [
            {
              Header: '',
              accessor: 'paid',
              Cell: ({ row: { original: row } }) => (
                <div className={styles.invoice_mobile_card}>
                  <div className={styles.invoice_mobile_top}>
                    <div>
                      <div className={styles.invoice_mobile_date}>{dateFormat(row.date)}</div>
                      <div className={styles.invoice_mobile_type}>{movementLabel(row)}</div>
                    </div>
                    <span className={styles.invoice_mobile_amount}>
                      {(row.total / 100).toFixed(2).replace('.', ',') + ' €'}
                    </span>
                  </div>
                  {row.paid ? (
                    <span className={styles.invoice_mobile_type}>
                      {dayjs(row.status_transitions.paid_at * 1000).format('DD/MM/YYYY')}
                    </span>
                  ) : movementStatus(row).processing ? (
                    <span className={styles.invoice_processing_status}>
                      {movementStatus(row).label}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={styles.invoice_pending_status}
                      onClick={scrollToRecovery}
                    >
                      {movementStatus(row).label}
                    </button>
                  )}
                </div>
              ),
            },
          ],
    [isMobile, t],
  );

  useEffect(() => {
    dispatch(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}, null, { skipLoader: true }));
  }, [dispatch]);

  useEffect(() => {
    if (!unpaidInvoiceUrl) {
      setPaymentStarted(false);
      setIsRefreshingPayment(false);
    }
  }, [unpaidInvoiceUrl]);

  useEffect(() => {
    if (Object.values(products || {}).length === 0) return;

    const productIds = resolvePlanCatalogIds(products, [
      subscriptionStatus?.currentPriceId,
    ]);
    if (productIds.length) {
      dispatch(settingsActions.GET_CUSTOMER_PLAN.REQUEST({ productIds }));
    }
  }, [products, subscriptionStatus?.currentPriceId, dispatch]);

  useEffect(() => {
    if (plans.length > 0 || productsLoading || !subscriptionStatus?.currentPriceId) return;
    if (products?.planMode === 'custom') {
      dispatch(
        settingsActions.GET_CUSTOMER_PLAN.REQUEST({
          productIds: [subscriptionStatus.currentPriceId],
        }),
      );
    }
  }, [
    plans.length,
    productsLoading,
    products?.planMode,
    subscriptionStatus?.currentPriceId,
    dispatch,
  ]);

  const stripeClientId = products?.idCustomerStripe || user?.idCostumerStripe;
  const needsBillingSetup = !productsLoading && !stripeClientId;

  const handleBillingSetupComplete = () => {
    dispatch(authActions.FETCH_SALON.REQUEST());
    dispatch(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!stripeClientId || productsLoading) return;

    dispatch(settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST({}, null, { skipLoader: true }));
    dispatch(settingsActions.GET_CUSTOMER_BILL.REQUEST({ clientId: stripeClientId }, null, { skipLoader: true }));
    dispatch(settingsActions.GET_CUSTOMER_INVOICE.REQUEST({ clientId: stripeClientId }, null, { skipLoader: true }));
  }, [stripeClientId, productsLoading, dispatch]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return undefined;

    if (checkout !== 'success') {
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
      return undefined;
    }

    if (!stripeClientId) return undefined;

    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });

    dispatch(
      settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST({}, () => {
        dispatch(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}));
        dispatch(
          settingsActions.GET_CUSTOMER_BILL.REQUEST(
            { clientId: stripeClientId },
            null,
            { skipLoader: true },
          ),
        );
        dispatch(
          settingsActions.GET_CUSTOMER_INVOICE.REQUEST(
            { clientId: stripeClientId },
            null,
            { skipLoader: true },
          ),
        );
      }),
    );
    return undefined;
  }, [searchParams, setSearchParams, stripeClientId, dispatch]);

  const handlePressPayment = () => {
    let newWindow;
    if (!localStorage.getItem('fromApp')) newWindow = window.open('', '_blank');

    if (!billingPortal) {
      return dispatch(
        settingsActions.CREATE_CUSTOMER_PORTAL.REQUEST(
          { clientId: stripeClientId },
          ({ data }) => {
            if (!fromApp) newWindow.location.href = data;
            sendMessageToApp('app:url', { url: data });
          },
        ),
      );
    }
    if (!fromApp) newWindow.location.href = billingPortal;
    sendMessageToApp('app:url', { url: billingPortal });
  };

  const openCheckoutUrl = (url) => {
    if (!url) return;
    if (fromApp) {
      sendMessageToApp('app:url', { url });
      return;
    }
    window.location.assign(url);
  };

  const handleSelectPlan = (priceId) => {
    if (!stripeClientId) return;

    dispatch(
      settingsActions.CREATE_SUBSCRIPTION.REQUEST(
        {
          clientId: stripeClientId,
          priceId,
          returnUrl: window.location.href.split('#')[0],
        },
        (data) => {
          if (data?.checkoutUrl) {
            openCheckoutUrl(data.checkoutUrl);
            return;
          }
          dispatch(settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST({}));
        },
      ),
    );
  };

  const handlePayUnpaidInvoice = () => {
    if (!unpaidInvoiceUrl) return;
    setPaymentStarted(true);
    if (fromApp) {
      sendMessageToApp('app:url', { url: unpaidInvoiceUrl });
      return;
    }
    window.open(unpaidInvoiceUrl, '_blank');
  };

  const handleRefreshAfterPayment = () => {
    if (!stripeClientId || isRefreshingPayment) return;
    setIsRefreshingPayment(true);
    setPaymentStarted(true);

    const finish = () => setIsRefreshingPayment(false);
    const safetyTimer = window.setTimeout(finish, 12000);

    dispatch(
      settingsActions.GET_CUSTOMER_INVOICE.REQUEST(
        { clientId: stripeClientId },
        () => {
          dispatch(
            settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST(
              {},
              () => {
                dispatch(
                  settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST(
                    {},
                    () => {
                      window.clearTimeout(safetyTimer);
                      finish();
                    },
                    { skipLoader: true },
                  ),
                );
              },
              { skipLoader: true },
            ),
          );
        },
        { skipLoader: true },
      ),
    );
  };

  const recoveryActiveStep = paymentStarted || invoicePending ? 2 : 1;
  // Recovery: abbonamento locked, oppure fattura da pagare.
  // Scaduto con sub/customer Stripe e fattura aperta: solo fattura (niente scelta piani).
  const accessExpired = Number.isFinite(daysLeftNum) && daysLeftNum <= 0;
  const expiredWithStripeInvoice =
    accessExpired &&
    Boolean(stripeClientId) &&
    (canPayNow || Boolean(openInvoice));
  const recoveryMode =
    isBillingPortal ||
    (canPayNow && !sepaWaitingInGrace) ||
    expiredWithStripeInvoice;
  // RID: fallito Stripe, oppure locked (grace scaduta / addebito non andato a buon fine)
  const sepaFailed =
    recoveryMode &&
    isSepaMethod &&
    (isPaymentFailed(unpaidPaymentStatus) || isBillingPortal);
  // Aggiorna metodo: RID fallito/locked, oppure scaduto senza fattura pagabile
  const forceUpdateMethod =
    sepaFailed || (isBillingPortal && !canPayNow);
  const allMovements = invoices?.invoices || [];
  const recentMovements = showAllMovements ? allMovements : allMovements.slice(0, RECENT_COUNT);

  return (
    <div
      className={clx(styles.payment_container, {
        [styles.payment_container_mobile]: isMobile,
        [styles.payment_container_is_bill]: isBillingPortal,
      })}
    >
      {!isBillingPortal ? (
        <div className={styles.header}>
          <TitleSectionBack
            title={t('settings.payments.title')}
            description={
              !stripeClientId
                ? t('settings.payments.billingSetup.pageDescription')
                : t('settings.payments.description')
            }
          />
        </div>
      ) : (
        <div className={styles.header}>
          <TitleSectionBack
            title={t('settings.payments.title')}
            description={t('settings.payments.subscription.expired')}
          />
          <button
            type="button"
            className={styles.logout_button}
            onClick={() => setIsLogoutModalOpen(true)}
          >
            <LogOut size={16} strokeWidth={2.2} aria-hidden />
            {t('settings.settingsList.cards.logout.title')}
          </button>
          <ConfirmModal
            isModalOpen={isLogoutModalOpen}
            setIsModalOpen={setIsLogoutModalOpen}
            title={t('settings.settingsList.modals.logout.title')}
            subtitle={t('settings.settingsList.modals.logout.subtitle')}
            Icon={Exit}
            variant="danger"
            cancelLabel={t('common.actions.cancel')}
            confirmLabel={t('common.actions.logout')}
            action={() =>
              dispatch(
                authActions.LOG_OUT.REQUEST({}, () => {
                  window.location.href = ROUTES.SIGN_IN;
                }),
              )
            }
          />
        </div>
      )}

      <div className={styles.payment_container_body}>
        {showRenewalBanner ? (
          <SubscriptionRenewalBanner
            daysLeft={daysLeft}
            expiryDate={products?.scadenza}
          />
        ) : null}

        {sepaWaitingInGrace && openInvoice ? (
          <aside className={styles.sepa_banner} role="status">
            <span className={styles.sepa_banner_icon} aria-hidden="true">
              <Landmark size={20} strokeWidth={2.2} />
            </span>
            <div className={styles.sepa_banner_copy}>
              <span className={styles.sepa_banner_badge}>
                {t('settings.payments.subscription.sepaProcessing.badge')}
              </span>
              <h3 className={styles.sepa_banner_title}>
                {t('settings.payments.subscription.sepaProcessing.title')}
              </h3>
              <p className={styles.sepa_banner_text}>
                {t('settings.payments.subscription.sepaProcessing.subtitle', {
                  amount: unpaidAmountLabel,
                  date: dateFormat(openInvoice.date),
                })}
                {expectedDebitDate ? (
                  <>
                    {' '}
                    <strong>
                      {t('settings.payments.subscription.sepaProcessing.expected', {
                        date: dayjs(expectedDebitDate).format('DD/MM/YYYY'),
                      })}
                    </strong>
                  </>
                ) : null}
              </p>
            </div>
            <button
              type="button"
              className={styles.sepa_banner_refresh}
              onClick={handleRefreshAfterPayment}
              disabled={isRefreshingPayment}
            >
              <RefreshCw
                size={14}
                strokeWidth={2.4}
                aria-hidden
                className={clx({ [styles.spin]: isRefreshingPayment })}
              />
              {isRefreshingPayment
                ? t('settings.payments.subscription.unpaidBanner.refreshing')
                : t('settings.payments.subscription.sepaProcessing.refresh')}
            </button>
          </aside>
        ) : null}

        {recoveryMode ? (
          <div className={styles.focus}>
            <section id="payments-recovery" className={styles.hero} role="alert">
              <span className={styles.hero_badge}>
                {t('settings.payments.subscription.unpaidBanner.badge')}
              </span>
              <h2 className={styles.hero_title}>
                {forceUpdateMethod
                  ? isSepaMethod
                    ? t('settings.payments.subscription.unpaidBanner.sepaFailedTitle')
                    : t('settings.payments.subscription.unpaidBanner.lockedTitle')
                  : t('settings.payments.subscription.unpaidBanner.title')}
              </h2>
              {forceUpdateMethod ? (
                <p className={styles.hero_invoice}>
                  {isSepaMethod
                    ? t('settings.payments.subscription.unpaidBanner.sepaFailedSubtitle')
                    : t('settings.payments.subscription.unpaidBanner.lockedSubtitle')}
                </p>
              ) : null}
              {unpaidAmountLabel && (payableInvoice || openInvoice) ? (
                <p className={styles.hero_invoice}>
                  {t('settings.payments.subscription.unpaidBanner.invoiceLine', {
                    date: dateFormat((payableInvoice || openInvoice).date),
                    amount: unpaidAmountLabel,
                  })}
                </p>
              ) : null}

              <div className={styles.hero_actions}>
                {forceUpdateMethod ? (
                  <button type="button" className={styles.hero_cta} onClick={handlePressPayment}>
                    <Landmark size={16} strokeWidth={2.4} aria-hidden />
                    {t('settings.payments.subscription.unpaidBanner.sepaFailedCta')}
                  </button>
                ) : showPayNowCta ? (
                  <button type="button" className={styles.hero_cta} onClick={handlePayUnpaidInvoice}>
                    <Zap size={16} strokeWidth={2.4} aria-hidden />
                    {t('settings.payments.subscription.unpaidBanner.cta')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={clx(styles.hero_cta_secondary, {
                    [styles.hero_cta]: !showPayNowCta && !forceUpdateMethod,
                  })}
                  onClick={handleRefreshAfterPayment}
                  disabled={isRefreshingPayment}
                >
                  <RefreshCw
                    size={15}
                    strokeWidth={2.4}
                    aria-hidden
                    className={clx({ [styles.spin]: isRefreshingPayment })}
                  />
                  {isRefreshingPayment
                    ? t('settings.payments.subscription.unpaidBanner.refreshing')
                    : t('settings.payments.subscription.unpaidBanner.refreshCta')}
                </button>
              </div>

              <p className={styles.hero_hint}>
                {paymentStarted || invoicePending
                  ? t('settings.payments.subscription.unpaidBanner.paymentStartedHint')
                  : t('settings.payments.subscription.unpaidBanner.stripeHint')}
              </p>

              <ol className={styles.hero_steps} aria-label={t('settings.payments.subscription.unpaidBanner.stepsLabel')}>
                {[1, 2, 3].map((step) => (
                  <li
                    key={step}
                    className={clx(styles.hero_step, {
                      [styles.hero_step_active]: recoveryActiveStep === step,
                      [styles.hero_step_done]: recoveryActiveStep > step,
                    })}
                  >
                    <span className={styles.hero_step_index}>
                      {recoveryActiveStep > step ? <Check size={11} strokeWidth={3} aria-hidden /> : step}
                    </span>
                    {t(`settings.payments.subscription.unpaidBanner.stepShort${step}`)}
                  </li>
                ))}
              </ol>
            </section>

            {recentMovements.length > 0 ? (
              <section className={styles.recent}>
                <div className={styles.recent_head}>
                  <h4 className={styles.section_label}>{t('settings.payments.movements.recent')}</h4>
                  {allMovements.length > RECENT_COUNT ? (
                    <button
                      type="button"
                      className={styles.recent_toggle}
                      onClick={() => setShowAllMovements((value) => !value)}
                    >
                      {showAllMovements
                        ? t('settings.payments.movements.showLess')
                        : t('settings.payments.movements.viewAll')}
                      <ChevronRight
                        size={14}
                        strokeWidth={2.4}
                        aria-hidden
                        className={clx(styles.recent_toggle_icon, {
                          [styles.recent_toggle_icon_open]: showAllMovements,
                        })}
                      />
                    </button>
                  ) : null}
                </div>
                <ul className={styles.recent_list}>
                  {recentMovements.map((row) => (
                    <li key={row.id} className={styles.recent_row}>
                      <span className={styles.recent_date}>{dateFormat(row.date)}</span>
                      <span className={styles.recent_desc}>{movementLabel(row)}</span>
                      <span className={styles.recent_amount}>
                        {(row.total / 100).toFixed(2).replace('.', ',')} €
                      </span>
                      {(() => {
                        const status = movementStatus(row);
                        return (
                          <span
                            className={clx(styles.recent_status, {
                              [styles.recent_status_pending]: status.pending,
                              [styles.recent_status_processing]: status.processing,
                            })}
                          >
                            {status.label}
                          </span>
                        );
                      })()}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <details className={styles.details}>
              <summary className={styles.details_summary}>
                <span>{t('settings.payments.subscription.unpaidBanner.detailsSummary')}</span>
                <ChevronRight size={16} strokeWidth={2.4} aria-hidden className={styles.details_chevron} />
              </summary>
              <div className={styles.details_body}>
                <div className={styles.details_block}>
                  <h4 className={styles.section_label}>{t('settings.payments.paymentMethod.title')}</h4>
                  <Cards paymentMethod={paymentMethod} handlePress={handlePressPayment} isMobile={isMobile} />
                </div>
                <div className={styles.details_block}>
                  <h4 className={styles.section_label}>{t('settings.payments.billingInfo.title')}</h4>
                  <Invoice
                    informations={billInformation}
                    handlePress={handlePressPayment}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            </details>
          </div>
        ) : null}

        {!recoveryMode ? (
        <div id="payments-subscription-section" className={styles.subscription_section}>
          {productsLoading && !stripeClientId && (
            <div className={styles.page_loading} role="status">
              {t('settings.payments.loading')}
            </div>
          )}

          {needsBillingSetup && (
            <>
              <ol className={styles.wizard} aria-label={t('settings.payments.billingSetup.step')}>
                <li className={clx(styles.wizard_step, styles.wizard_step_active)}>
                  <span className={styles.wizard_index}>1</span>
                  <span className={styles.wizard_label}>
                    {t('settings.payments.billingSetup.stepShort')}
                  </span>
                </li>
                <li className={styles.wizard_rule} aria-hidden="true" />
                <li className={styles.wizard_step}>
                  <span className={styles.wizard_index}>2</span>
                  <span className={styles.wizard_label}>
                    {t('settings.payments.plansPreview.stepShort')}
                  </span>
                </li>
              </ol>
              <BillingSetup onSuccess={handleBillingSetupComplete} />
            </>
          )}

          {!!stripeClientId && (
            <SubscriptionPlans
              plans={plans}
              subscriptionStatus={subscriptionStatus}
              products={productsWithInvoiceFlag}
              clientId={stripeClientId}
              billingSetupRequired={false}
              collectPaymentOnConfirm={collectPaymentOnConfirm}
              isLoading={productsLoading || plansLoading}
              isSubmitting={subscriptionSubmitting}
              onSelectPlan={handleSelectPlan}
              errorMessage={subscriptionError || productsError}
              onRetry={() => {
                dispatch(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}));
                const productIds = resolvePlanCatalogIds(products || {}, [
                  subscriptionStatus?.currentPriceId,
                ]);
                if (productIds.length) {
                  dispatch(settingsActions.GET_CUSTOMER_PLAN.REQUEST({ productIds }));
                }
              }}
              onFocusPayment={scrollToRecovery}
            />
          )}
        </div>
        ) : null}

        {!recoveryMode && !!stripeClientId && !productsLoading && (
          <>
            <div className={styles.section_card}>
              <h4 className={styles.section_label}>{t('settings.payments.billing.title')}</h4>
              <div className={clx(styles.billing_grid, { [styles.billing_grid_mobile]: isMobile })}>
                <div className={styles.billing_block}>
                  <span className={styles.billing_block_label}>
                    {t('settings.payments.paymentMethod.title')}
                  </span>
                  <Cards paymentMethod={paymentMethod} handlePress={handlePressPayment} isMobile />
                </div>
                <div className={styles.billing_block}>
                  <span className={styles.billing_block_label}>
                    {t('settings.payments.billingInfo.title')}
                  </span>
                  <Invoice informations={billInformation} handlePress={handlePressPayment} isMobile />
                </div>
              </div>
            </div>

            <div className={styles.section_card}>
              <h4 className={styles.section_label}>{t('settings.payments.movements.title')}</h4>
              <Table
                columns={columns}
                data={invoices?.invoices || []}
                isMobile={isMobile}
                pageRows={5}
                paginationPosition="both"
                entityId="payments-invoices"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Payments;
